import type {
  BillCategory,
  CapabilityGate,
  ExpenseSplit,
  LedgerTransaction,
  MoneyRequest,
} from '../types/finance'

export const capabilityGates: CapabilityGate[] = [
  {
    id: 'cap-1',
    capability: 'Account funding',
    status: 'gated',
    performedBy: 'Licensed payment processor',
    note: 'Funding is verified server-side against the provider before any ledger entry is written. Not enabled in this release.',
  },
  {
    id: 'cap-2',
    capability: 'Stored value / balance',
    status: 'gated',
    performedBy: 'To be determined by regulatory structure',
    note: 'Balances shown here are internal records of entitlement. Campuzon does not hold customer funds or issue stored value.',
  },
  {
    id: 'cap-3',
    capability: 'Transfers between users',
    status: 'gated',
    performedBy: 'Licensed payment institution',
    note: 'Send, request and split are modelled end to end but cannot move money until the regulated structure is in place.',
  },
  {
    id: 'cap-4',
    capability: 'Marketplace escrow',
    status: 'gated',
    performedBy: 'Licensed escrow or payment provider',
    note: 'Order state machine and escrow accounting are live; the holding of funds is not performed by Campuzon.',
  },
  {
    id: 'cap-5',
    capability: 'Provider payouts',
    status: 'gated',
    performedBy: 'Bank / licensed payout provider',
    note: 'Settlement rules, fees and refund windows are modelled. Disbursement requires provider onboarding.',
  },
  {
    id: 'cap-6',
    capability: 'Bill payment and airtime',
    status: 'gated',
    performedBy: 'Licensed bill aggregator',
    note: 'Category → provider → product configuration is complete. Reserve, finalise and reverse states are modelled.',
  },
  {
    id: 'cap-7',
    capability: 'Transaction records and ledger',
    status: 'live',
    performedBy: 'Campuzon',
    note: 'Double-entry records, references, reconciliation and audit history are Campuzon platform infrastructure.',
  },
]

export const transactions: LedgerTransaction[] = [
  {
    id: 'txn-1',
    reference: 'TXN-000481',
    kind: 'funding',
    domain: 'platform',
    narrative: 'Card funding via payment processor',
    counterparty: 'Licensed payment processor',
    at: '10 Aug 2026, 08:10',
    state: 'SETTLED',
    provider: 'Payment processor',
    providerReference: 'PSP-9f13ac',
    entries: [
      { account: 'counterparty:external', direction: 'debit', amountNaira: 40000 },
      { account: 'user:available', direction: 'credit', amountNaira: 40000 },
    ],
  },
  {
    id: 'txn-2',
    reference: 'TXN-000482',
    kind: 'escrow_hold',
    domain: 'services',
    narrative: 'ORD-40128 — motivation letter review held in escrow',
    counterparty: '@tokoro',
    at: '10 Aug 2026, 08:12',
    state: 'RESERVED',
    provider: 'Escrow provider',
    providerReference: 'ESC-0021a',
    entries: [
      { account: 'user:available', direction: 'debit', amountNaira: 18000 },
      { account: 'user:escrow', direction: 'credit', amountNaira: 18000 },
    ],
  },
  {
    id: 'txn-3',
    reference: 'TXN-000470',
    kind: 'order_payment',
    domain: 'knowledge',
    narrative: 'ORD-39880 — 30 day rental, Control Systems Engineering',
    counterparty: 'Akoka Academic Press',
    at: '28 Jul 2026, 11:03',
    state: 'SETTLED',
    provider: 'Payment processor',
    providerReference: 'PSP-7ac221',
    entries: [
      { account: 'user:available', direction: 'debit', amountNaira: 2500 },
      { account: 'provider:settlement', direction: 'credit', amountNaira: 2500 },
    ],
  },
  {
    id: 'txn-4',
    reference: 'TXN-000471',
    kind: 'fee',
    domain: 'knowledge',
    narrative: 'Platform fee on ORD-39880',
    counterparty: 'Campuzon',
    at: '28 Jul 2026, 11:03',
    state: 'SETTLED',
    provider: null,
    providerReference: null,
    entries: [
      { account: 'provider:settlement', direction: 'debit', amountNaira: 250 },
      { account: 'platform:fees', direction: 'credit', amountNaira: 250 },
    ],
  },
  {
    id: 'txn-5',
    reference: 'TXN-000455',
    kind: 'transfer',
    domain: 'social',
    narrative: 'Sent to @ifeoma.e — share of study pack',
    counterparty: '@ifeoma.e',
    at: '24 Jul 2026, 19:40',
    state: 'SETTLED',
    provider: 'Licensed payment institution',
    providerReference: 'TRF-11c802',
    entries: [
      { account: 'user:available', direction: 'debit', amountNaira: 3000 },
      { account: 'counterparty:external', direction: 'credit', amountNaira: 3000 },
    ],
  },
  {
    id: 'txn-6',
    reference: 'TXN-000448',
    kind: 'bill_payment',
    domain: 'bills',
    narrative: 'MTN 10GB monthly data — 0806 555 0142',
    counterparty: 'MTN Nigeria',
    at: '20 Jul 2026, 07:15',
    state: 'FAILED',
    provider: 'Bill aggregator',
    providerReference: 'BIL-4410de',
    entries: [
      { account: 'user:available', direction: 'debit', amountNaira: 4500 },
      { account: 'counterparty:external', direction: 'credit', amountNaira: 4500 },
    ],
  },
  {
    id: 'txn-7',
    reference: 'TXN-000449',
    kind: 'reversal',
    domain: 'bills',
    narrative: 'Reversal of TXN-000448 — biller declined the request',
    counterparty: 'Bill aggregator',
    at: '20 Jul 2026, 07:18',
    state: 'REVERSED',
    provider: 'Bill aggregator',
    providerReference: 'BIL-4410de-R',
    correctsReference: 'TXN-000448',
    entries: [
      { account: 'counterparty:external', direction: 'debit', amountNaira: 4500 },
      { account: 'user:available', direction: 'credit', amountNaira: 4500 },
    ],
  },
  {
    id: 'txn-8',
    reference: 'TXN-000432',
    kind: 'order_payment',
    domain: 'events',
    narrative: 'ORD-38610 — Campuzon Hackathon ticket',
    counterparty: 'Campuzon Technologies',
    at: '11 Aug 2026, 12:01',
    state: 'PENDING',
    provider: 'Payment processor',
    providerReference: 'PSP-b0092f',
    entries: [
      { account: 'user:available', direction: 'debit', amountNaira: 2500 },
      { account: 'user:pending', direction: 'credit', amountNaira: 2500 },
    ],
  },
  {
    id: 'txn-9',
    reference: 'TXN-000401',
    kind: 'gift',
    domain: 'social',
    narrative: 'Gift received from @gracen — congratulations on the shortlist',
    counterparty: '@gracen',
    at: '5 Aug 2026, 21:12',
    state: 'SETTLED',
    provider: 'Licensed payment institution',
    providerReference: 'TRF-0f7712',
    entries: [
      { account: 'counterparty:external', direction: 'debit', amountNaira: 10000 },
      { account: 'user:available', direction: 'credit', amountNaira: 10000 },
    ],
  },
  {
    id: 'txn-10',
    reference: 'TXN-000390',
    kind: 'refund',
    domain: 'services',
    narrative: 'Partial refund on ORD-38904 pending dispute outcome',
    counterparty: '@seunprints',
    at: '23 Jul 2026, 09:02',
    state: 'PENDING',
    provider: 'Escrow provider',
    providerReference: 'ESC-0019b',
    entries: [
      { account: 'user:escrow', direction: 'debit', amountNaira: 4500 },
      { account: 'user:pending', direction: 'credit', amountNaira: 4500 },
    ],
  },
  {
    id: 'txn-11',
    reference: 'TXN-000383',
    kind: 'escrow_hold',
    domain: 'services',
    narrative: 'ORD-38904 — CAD drawing held in escrow',
    counterparty: '@seunprints',
    at: '20 Jul 2026, 10:40',
    state: 'RESERVED',
    provider: 'Escrow provider',
    providerReference: 'ESC-0019a',
    entries: [
      { account: 'user:available', direction: 'debit', amountNaira: 9000 },
      { account: 'user:escrow', direction: 'credit', amountNaira: 9000 },
    ],
  },
  {
    id: 'txn-12',
    reference: 'TXN-000377',
    kind: 'funding',
    domain: 'platform',
    narrative: 'Bank transfer funding',
    counterparty: 'Licensed payment processor',
    at: '18 Jul 2026, 14:22',
    state: 'SETTLED',
    provider: 'Payment processor',
    providerReference: 'PSP-3e91b7',
    entries: [
      { account: 'counterparty:external', direction: 'debit', amountNaira: 25000 },
      { account: 'user:available', direction: 'credit', amountNaira: 25000 },
    ],
  },
]

export const moneyRequests: MoneyRequest[] = [
  {
    id: 'req-1',
    handle: 'ifeoma.e',
    displayName: 'Ifeoma Eze',
    amountNaira: 5000,
    reason: 'Share of the SESA past-questions pack',
    direction: 'incoming',
    state: 'awaiting',
    at: '2 days ago',
  },
  {
    id: 'req-2',
    handle: 'danielu',
    displayName: 'Daniel Uzo',
    amountNaira: 3500,
    reason: 'Hackathon transport',
    direction: 'outgoing',
    state: 'awaiting',
    at: '4 days ago',
  },
  {
    id: 'req-3',
    handle: 'tolua',
    displayName: 'Tolu Adeleke',
    amountNaira: 7500,
    reason: 'Printing for the group project',
    direction: 'incoming',
    state: 'settled',
    at: '1 week ago',
  },
]

export const expenseSplits: ExpenseSplit[] = [
  {
    id: 'spl-1',
    title: 'Room B-204 shared internet — August',
    totalNaira: 20000,
    createdBy: 'amarachi',
    at: '3 days ago',
    participants: [
      { handle: 'amarachi', displayName: 'You', shareNaira: 5000, state: 'settled' },
      { handle: 'ifeoma.e', displayName: 'Ifeoma E.', shareNaira: 5000, state: 'settled' },
      { handle: 'danielu', displayName: 'Daniel U.', shareNaira: 5000, state: 'awaiting' },
      { handle: 'tolua', displayName: 'Tolu A.', shareNaira: 5000, state: 'awaiting' },
    ],
  },
  {
    id: 'spl-2',
    title: 'Engineering Week group dinner',
    totalNaira: 36000,
    createdBy: 'gracen',
    at: '1 week ago',
    participants: [
      { handle: 'amarachi', displayName: 'You', shareNaira: 12000, state: 'settled' },
      { handle: 'gracen', displayName: 'Grace N.', shareNaira: 12000, state: 'settled' },
      { handle: 'tokoro', displayName: 'Tosin O.', shareNaira: 12000, state: 'settled' },
    ],
  },
]

export const billCategories: BillCategory[] = [
  {
    id: 'bc-airtime',
    name: 'Airtime',
    providers: [
      {
        id: 'bp-mtn',
        name: 'MTN',
        products: [
          { id: 'bpr-1', name: 'Airtime top-up', priceNaira: null, identifierLabel: 'Phone number' },
        ],
      },
      {
        id: 'bp-airtel',
        name: 'Airtel',
        products: [
          { id: 'bpr-2', name: 'Airtime top-up', priceNaira: null, identifierLabel: 'Phone number' },
        ],
      },
      {
        id: 'bp-glo',
        name: 'Glo',
        products: [
          { id: 'bpr-3', name: 'Airtime top-up', priceNaira: null, identifierLabel: 'Phone number' },
        ],
      },
      {
        id: 'bp-9mobile',
        name: '9mobile',
        products: [
          { id: 'bpr-4', name: 'Airtime top-up', priceNaira: null, identifierLabel: 'Phone number' },
        ],
      },
    ],
  },
  {
    id: 'bc-data',
    name: 'Data',
    providers: [
      {
        id: 'bp-mtn-d',
        name: 'MTN',
        products: [
          { id: 'bpr-5', name: '2GB — 30 days', priceNaira: 1500, identifierLabel: 'Phone number' },
          { id: 'bpr-6', name: '10GB — 30 days', priceNaira: 4500, identifierLabel: 'Phone number' },
          { id: 'bpr-7', name: '25GB — 30 days', priceNaira: 9000, identifierLabel: 'Phone number' },
        ],
      },
      {
        id: 'bp-airtel-d',
        name: 'Airtel',
        products: [
          { id: 'bpr-8', name: '3GB — 30 days', priceNaira: 1500, identifierLabel: 'Phone number' },
          { id: 'bpr-9', name: '11GB — 30 days', priceNaira: 5000, identifierLabel: 'Phone number' },
        ],
      },
    ],
  },
  {
    id: 'bc-electricity',
    name: 'Electricity',
    providers: [
      {
        id: 'bp-ekedc',
        name: 'Eko Electricity (EKEDC)',
        products: [
          { id: 'bpr-10', name: 'Prepaid token', priceNaira: null, identifierLabel: 'Meter number' },
        ],
      },
      {
        id: 'bp-ikedc',
        name: 'Ikeja Electric (IKEDC)',
        products: [
          { id: 'bpr-11', name: 'Prepaid token', priceNaira: null, identifierLabel: 'Meter number' },
        ],
      },
    ],
  },
  {
    id: 'bc-internet',
    name: 'Internet and TV',
    providers: [
      {
        id: 'bp-fibreone',
        name: 'Fibre One',
        products: [
          { id: 'bpr-12', name: 'Home 30Mbps — monthly', priceNaira: 22000, identifierLabel: 'Account number' },
        ],
      },
      {
        id: 'bp-dstv',
        name: 'DStv',
        products: [
          { id: 'bpr-13', name: 'Compact — monthly', priceNaira: 19000, identifierLabel: 'Smartcard number' },
        ],
      },
    ],
  },
]

export const reconciliation = [
  { source: 'Payment processor', matched: 1284, unmatched: 3, difference: 0, checked: 'Today 09:00' },
  { source: 'Bank settlement account', matched: 1281, unmatched: 6, difference: 4500, checked: 'Today 09:00' },
  { source: 'Escrow provider', matched: 412, unmatched: 1, difference: 0, checked: 'Today 08:30' },
  { source: 'Bill aggregator', matched: 908, unmatched: 2, difference: 0, checked: 'Today 08:30' },
]
